import type {
  IneligibilityReasonCode,
  RewardRedemptionCodeResponse,
  RewardRedemptionGameResponse,
  RewardRedemptionRequest,
  RewardRedemptionUrlResponse,
  Rewards,
} from '@/reward-types'
import fetcher from '@/services/fetcher'
import type { CsiResponse, Nullable, PortalApiResponse } from '@/types'
import { toPortalUrl } from '@/utils/url-generation-utils'
import { defineStore } from 'pinia'
import { useToast } from 'vue-toastification'

import { useUserStore } from './user'

type RewardsStoreState = {
  rewards: Map<string, Rewards>
  fetchPromise: Promise<Array<Rewards>> | null
}

export const useRewardsStore = defineStore('rewards', {
  state: (): RewardsStoreState => ({
    rewards: new Map<string, Rewards>(),
    fetchPromise: null,
  }),

  getters: {
    /**
     * Returns all rewards as an array.
     *
     * @returns An array of all Rewards.
     */
    rewardsArray(): Array<Rewards> {
      return Array.from(this.rewards.values())
    },

    /**
     * Returns all favourite rewards.
     *
     * @returns An array of Rewards that are marked as favourites.
     */
    favouriteRewards(): Array<Rewards> {
      return this.rewardsArray.filter((reward: Rewards) => reward.is_favourite)
    },
  },

  actions: {
    /**
     * Fetches rewards from the portal API and stores them in the rewards map.
     *
     * @returns A promise that resolves to an array of Rewards.
     */
    async fetchRewards(force: boolean = false): Promise<Array<Rewards>> {
      try {
        if (force) {
          this.rewards.clear()
        }

        if (this.fetchPromise) {
          return this.fetchPromise
        }

        this.fetchPromise = (async () => {
          try {
            const { data, error } = await fetcher.get<CsiResponse<Rewards[]>>(
              toPortalUrl('rewards'),
              true
            )

            if (error.value || !data.value?.csi_content) {
              return []
            }

            data.value.csi_content.forEach((reward: Rewards) => {
              this.rewards.set(reward.code, reward)
            })

            return this.rewardsArray
          } catch (error: any) {
            console.error('Error fetching rewards:', error)
            return this.rewardsArray
          } finally {
            this.fetchPromise = null
          }
        })()

        return this.fetchPromise
      } catch (error: any) {
        // TODO: Add Datadog custom log
        console.error('Error fetching rewards:', error)

        return this.rewardsArray
      }
    },

    /**
     * Fetches a specific reward by its code from the portal API.
     *
     * @param {string} code
     * @returns A promise that resolves to the Rewards object or null if an error occurs.
     */
    async fetchReward<T extends Rewards>(code: string): Promise<Nullable<T>> {
      const toast = useToast()

      try {
        const { data, error } = await fetcher.get<T>(
          toPortalUrl(`rewards/${code}`),
          true
        )

        if (error.value || !data.value) {
          toast.error(
            error.value?.message ||
              'An error occurred when fetching the reward. Please try again later.'
          )
          return null
        }

        return data.value
      } catch (error: any) {
        // TODO: Add Datadog custom log
        console.error(`Error fetching reward ${code}:`, error)

        return (this.rewards.get(code) as T) ?? null
      }
    },

    /**
     * Redeems a reward based on the provided redemption request.
     *
     * @param {RewardRedemptionRequest} redemptionRequest
     * @returns A promise that resolves to the RewardRedemptionResponse or throws an exception if an error occurs.
     */
    async redeemReward<
      T extends
        | RewardRedemptionCodeResponse
        | RewardRedemptionUrlResponse
        | RewardRedemptionGameResponse,
    >(redemptionRequest: RewardRedemptionRequest): Promise<null | Array<T>> {
      const { data, error } = await fetcher.post<null | PortalApiResponse<{
        data: Array<T>
        user_data: {
          eligible_points: number
        }
      }>>(toPortalUrl(`rewards/redeem`), redemptionRequest, true)

      if (error.value || !data.value) {
        throw new Error(
          `An error occurred when redeeming the reward. ${
            error.value.errors?.value?.error
              ? this.eligibilityCodeToMessage(error.value.errors.value.error)
                  .long
              : ''
          } Please try again later.`
        )
      }

      const csiContent = data.value.csi_content

      if (!csiContent) {
        console.error('No CSI content in response:', data.value)
        return null
      }

      // Update eligible points
      const userStore = useUserStore()

      if (userStore.user) {
        userStore.user.eligible_points = csiContent.user_data.eligible_points
      }

      return csiContent.data ?? null
    },

    /**
     * Emails a reward using its idempotency key.
     *
     * @param {string} idempotencyKey
     * @returns A promise that resolves to true if the email was sent successfully, otherwise throws an exception.
     */
    async emailReward(idempotencyKey: string): Promise<boolean> {
      const { data, error } = await fetcher.post(
        toPortalUrl(`rewards/redeem/email`),
        {
          idempotencyKey,
        },
        true
      )

      if (error.value || !data.value) {
        throw new Error(
          `An error occurred when emailing your reward. ${
            error.value.errors?.value?.error
              ? this.eligibilityCodeToMessage(error.value.errors.value.error)
                  .long
              : ''
          } Please try again later.`
        )
      }

      return true
    },

    /**
     * Filters rewards by category.
     *
     * @param {string} category - The category to filter rewards by.
     * @returns An array of Rewards that match the specified category.
     */
    getRewardsByCategory(category: string): Array<Rewards> {
      return this.rewardsArray.filter((reward: Rewards) =>
        reward.categories.some((cat: string) =>
          cat.toLowerCase().includes(category.toLowerCase())
        )
      )
    },

    /**
     * Converts an eligibility reason code to a user-friendly message.
     *
     * @param {IneligibilityReasonCode} code
     * @returns {string} User-friendly message corresponding to the eligibility reason code.
     */
    eligibilityCodeToMessage(code?: IneligibilityReasonCode): {
      short: string
      long: string
    } {
      switch (code) {
        case 'idempotency_key_duplicate':
          return {
            short: 'Request Already Processed',
            long: 'This request has already been processed. Please try again later.',
          }
        case 'customer_not_eligible':
          return {
            short: 'Reward Not Eligible',
            long: 'You are not eligible for this reward.',
          }
        case 'insufficient_xp':
          return {
            short: 'Not Enough Points',
            long: 'You do not have enough points to redeem this reward.',
          }
        case 'limit_reached':
          return {
            short: 'Redeemed',
            long: 'You have reached the redemption limit for this reward.',
          }
        case 'option_not_available':
          return {
            short: 'Option Not Available',
            long: 'The selected option is not available.',
          }
        case 'option_not_eligible':
          return {
            short: 'Option Not Eligible',
            long: 'You are not eligible for the selected option.',
          }
        case 'option_not_valid':
          return {
            short: 'Option Not Available',
            long: 'The selected option is not valid.',
          }
        case 'reward_not_available':
          return {
            short: 'Reward Not Available',
            long: 'The selected reward is not available.',
          }
        case 'reward_out_of_stock':
          return {
            short: 'Out Of Stock',
            long: 'The selected reward is currently out of stock.',
          }
        case 'unknown_redemption_type':
          return {
            short: 'Unknown Redemption Type',
            long: 'The redemption type is unknown.',
          }
        case 'drawboost_not_configured':
          return {
            short: 'Draw Not Available',
            long: 'The draw is not available.',
          }
        case 'drawboost_not_open':
          return { short: 'Draw Not Open', long: 'The draw is not open.' }
        case 'drawboost_invalid':
          return {
            short: 'Invalid Draw Code',
            long: 'The draw code is invalid.',
          }
        case 'drawboost_insufficient_xp':
          return {
            short: 'Insufficient Points',
            long: 'You do not have enough points for the draw.',
          }
        case 'redemption_not_found':
          return {
            short: 'Redemption Not Found',
            long: 'The redemption could not be found.',
          }
        case 'instantwin_not_configured':
        case 'instantwin_not_allocatable':
          return {
            short: 'Game Not Available',
            long: 'This game is not currently available.',
          }
        case 'voucher_not_configured':
          return {
            short: 'No Vouchers Available',
            long: 'No vouchers are available.',
          }
        case 'voucher_no_denomination':
          return {
            short: 'No Vouchers Available',
            long: 'No vouchers are available.',
          }
        case 'voucher_not_available':
          return {
            short: 'No Vouchers Available',
            long: 'No vouchers are available.',
          }
        case 'frequencygame_not_available':
          return {
            short: 'Daily Spin Game Not Available',
            long: 'The daily spin game is not available.',
          }
        case 'artifact_creation_failed':
          return {
            short: 'Error Redeeming Reward',
            long: 'There was an error redeeming the reward. Please try again later.',
          }
        // Reward rule codes
        case 'unavailable':
          return {
            short: 'Currently Unavailable',
            long: 'The reward is currently unavailable.',
          }
        case 'out_of_stock':
          return {
            short: 'Out Of Stock',
            long: 'The reward is currently out of stock.',
          }
        case 'redemption_limit_reached':
          return {
            short: 'Redeemed',
            long: 'You have reached the redemption limit for this reward.',
          }
        case 'require_plan':
          return {
            short: 'Specific Plan Required',
            long: 'A specific plan is required to redeem this reward.',
          }
        case 'x_per_y':
          return {
            short: 'Redeemed',
            long: 'You have reached the redemption limit for this reward.',
          }
        case 'x_per_y_period':
          return {
            short: 'Redeemed',
            long: 'You have reached the redemption limit for this reward.',
          }
        // Reward option codes
        case 'option_unavailable':
          return {
            short: 'Currently Unavailable',
            long: 'The option is currently unavailable.',
          }
        case 'option_min_level':
          return {
            short: 'Minimum Level Not Met',
            long: 'You have not reached the minimum level required for this option.',
          }
        case 'option_min_points_cost':
          return {
            short: 'Not Enough Points',
            long: 'You do not have enough points for this option.',
          }
        default:
          return {
            short: 'Currently Unavailable',
            long: 'The reward is currently unavailable.',
          }
      }
    },
  },
})
